// src/components/sports/SportsSidebar.jsx
import React, { useState, useEffect } from 'react';
import { FootballAPI } from '../../services/footballApi';
import { FiChevronDown, FiChevronRight, FiX, FiFilter } from 'react-icons/fi';

const SportsSidebar = ({ selectedSport, onSportSelect, selectedDate, onLeagueSelect, isMobile, onClose }) => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [isLeaguesOpen, setIsLeaguesOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('sports'); // 'sports' or 'leagues'

  // Sports order - Football first, then Basketball, Tennis, etc.
  const sports = [
    { id: 'football', name: 'Football', icon: '⚽', color: 'bg-green-600' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', color: 'bg-orange-600' },
    { id: 'tennis', name: 'Tennis', icon: '🎾', color: 'bg-yellow-600' },
    { id: 'baseball', name: 'Baseball', icon: '⚾', color: 'bg-red-600' },
    { id: 'hockey', name: 'Ice Hockey', icon: '🏒', color: 'bg-blue-600' },
    { id: 'esports', name: 'Esports', icon: '🎮', color: 'bg-purple-600' },
    { id: 'cricket', name: 'Cricket', icon: '🏏', color: 'bg-emerald-600' },
    { id: 'rugby', name: 'Rugby', icon: '🏉', color: 'bg-amber-600' },
  ];

  // League priority order with World Cup first
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1,   // FIFA World Cup - FIRST
      2: 2,   // UEFA Champions League
      39: 3,  // Premier League
      140: 4, // La Liga
      135: 5, // Serie A
      78: 6,  // Bundesliga
      61: 7,  // Ligue 1
    };
    
    if (priorityMap[leagueId]) {
      return priorityMap[leagueId];
    }
    
    const name = leagueName.toLowerCase();
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    
    return 999;
  };

  // Fetch leagues when date changes and football is selected
  useEffect(() => {
    if (selectedSport === 'football' && selectedDate) {
      fetchLeagues();
    }
  }, [selectedSport, selectedDate]);

  const fetchLeagues = async () => {
    setLoading(true);
    try {
      const response = await FootballAPI.getLeagues(selectedDate);
      if (response.success && response.data) {
        const sortedLeagues = [...response.data].sort((a, b) => {
          const priorityA = getLeaguePriority(a.id, a.name);
          const priorityB = getLeaguePriority(b.id, b.name);
          return priorityA - priorityB;
        });
        setLeagues(sortedLeagues);
      }
    } catch (error) {
      // console.error('Failed to fetch leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueClick = (leagueId) => {
    const newSelected = selectedLeague === leagueId ? null : leagueId;
    setSelectedLeague(newSelected);
    if (onLeagueSelect) {
      onLeagueSelect(newSelected);
    }
    // On mobile, close sidebar after selection
    if (isMobile && onClose) {
      setTimeout(() => onClose(), 300);
    }
  };

  const handleSportSelect = (sportId) => {
    onSportSelect(sportId);
    if (sportId !== 'football') {
      setSelectedLeague(null);
      if (onLeagueSelect) {
        onLeagueSelect(null);
      }
    }
    // On mobile, close sidebar after selection
    if (isMobile && onClose && sportId !== 'football') {
      setTimeout(() => onClose(), 300);
    }
  };

  const toggleLeagues = () => {
    setIsLeaguesOpen(!isLeaguesOpen);
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  // Mobile Bottom Sheet Component
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
        
        {/* Bottom Sheet */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl z-50 animate-slide-up max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <FiFilter className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-bold text-white">Filters</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
            >
              <FiX className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Tab Bar */}
          <div className="flex border-b border-gray-800">
            <button
              onClick={() => setActiveTab('sports')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'sports'
                  ? 'text-blue-500 border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sports
            </button>
            {selectedSport === 'football' && (
              <button
                onClick={() => setActiveTab('leagues')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'leagues'
                    ? 'text-blue-500 border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Leagues ({leagues.length})
              </button>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'sports' ? (
              <div className="grid grid-cols-2 gap-2">
                {sports.map((sport) => (
                  <button
                    key={sport.id}
                    onClick={() => handleSportSelect(sport.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      selectedSport === sport.id
                        ? `${sport.color} text-white shadow-lg`
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <span className="text-2xl">{sport.icon}</span>
                    <span className="font-medium">{sport.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="text-sm text-gray-500 mt-3">Loading leagues...</p>
                  </div>
                ) : leagues.length > 0 ? (
                  leagues.map(league => {
                    const priority = getLeaguePriority(league.id, league.name);
                    const isTopLeague = priority <= 7;
                    const isWorldCup = league.id === 1 || league.name.toLowerCase().includes('world cup');
                    
                    return (
                      <button
                        key={league.id}
                        onClick={() => handleLeagueClick(league.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                          selectedLeague === league.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {league.logo ? (
                          <img 
                            src={league.logo} 
                            alt={league.name} 
                            className="w-6 h-6 object-contain"
                            onError={handleImageError}
                          />
                        ) : (
                          <span className="text-xl">{isWorldCup ? '🏆' : '⚽'}</span>
                        )}
                        <span className="flex-1 text-left font-medium">{league.name}</span>
                        {(isTopLeague || isWorldCup) && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            isWorldCup 
                              ? 'bg-yellow-500/20 text-yellow-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isWorldCup ? 'Featured' : 'Top'}
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No leagues available</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Apply Button for Mobile */}
          <div className="p-4 border-t border-gray-800">
            <button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
        
        <style jsx>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </>
    );
  }

  // Desktop Version
  return (
    <div className="bg-gray-900 rounded-xl p-4">
      <h3 className="text-lg font-bold text-white mb-4">Sports Categories</h3>
      
      {/* Sports List - Desktop */}
      <div className="space-y-1">
        {sports.map((sport) => (
          <React.Fragment key={sport.id}>
            <button
              onClick={() => handleSportSelect(sport.id)}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                selectedSport === sport.id
                  ? 'bg-blue-600 text-white'
                  : 'hover:bg-gray-800 text-gray-300'
              }`}
            >
              <span className="text-xl mr-3">{sport.icon}</span>
              <span className="text-sm sm:text-base">{sport.name}</span>
            </button>
            
            {/* Leagues Section - Desktop */}
            {sport.id === 'football' && selectedSport === 'football' && (
              <div className="ml-4 mt-2 mb-2 pl-2 border-l-2 border-gray-700">
                <button
                  onClick={toggleLeagues}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-800 transition-colors mb-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Leagues
                    </span>
                    <span className="text-xs text-gray-500">
                      ({leagues.length})
                    </span>
                  </div>
                  {isLeaguesOpen ? (
                    <FiChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <FiChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                {isLeaguesOpen && (
                  <div className="space-y-1 mt-1">
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                        <p className="text-xs text-gray-500 mt-2">Loading leagues...</p>
                      </div>
                    ) : leagues.length > 0 ? (
                      <div className="space-y-1 max-h-96 overflow-y-auto custom-scrollbar">
                        {leagues.map(league => {
                          const priority = getLeaguePriority(league.id, league.name);
                          const isTopLeague = priority <= 7;
                          const isWorldCup = league.id === 1 || league.name.toLowerCase().includes('world cup');
                          
                          return (
                            <button
                              key={league.id}
                              onClick={() => handleLeagueClick(league.id)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                                selectedLeague === league.id
                                  ? 'bg-blue-600/20 text-blue-400'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                              }`}
                            >
                              {league.logo ? (
                                <img 
                                  src={league.logo} 
                                  alt={league.name} 
                                  className="w-4 h-4 object-contain"
                                  onError={handleImageError}
                                />
                              ) : (
                                <span className="w-4 h-4 text-xs">
                                  {isWorldCup ? '🏆' : '📋'}
                                </span>
                              )}
                              <span className="truncate flex-1 text-left">
                                {league.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-xs text-gray-500">No leagues available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Quick Filters - Desktop */}
      {selectedSport === 'football' && (
        <div className="mt-6 pt-6 border-t border-gray-800">
          <h4 className="text-sm font-bold text-white mb-3">Quick Filters</h4>
          <div className="space-y-2">
            <button className="w-full text-left p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded flex items-center gap-2 transition-colors">
              <span>⭐</span>
              <span>Popular Matches</span>
            </button>
            <button className="w-full text-left p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded flex items-center gap-2 transition-colors">
              <span>🔥</span>
              <span>Live Now</span>
            </button>
            <button className="w-full text-left p-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded flex items-center gap-2 transition-colors">
              <span>💰</span>
              <span>High Odds</span>
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1f2937;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default SportsSidebar;